import { DialogService } from '@progress/kendo-angular-dialog';
import { Setting } from '@raptor-fe/core';
import { firstValueFrom } from 'rxjs';
import { BranchTimeZoneModalComponent } from '../modals/branch-time-zone-modal/branch-time-zone-modal.component';

/** Setting display name the settings endpoint identifies the branch time zone by. */
export const BRANCH_TIME_ZONE_SETTING_KEY = 'Branch Time Zone';

/** Setting enum member name the resolved settings attached to a branch are keyed by. */
const BRANCH_TIME_ZONE_SETTING_NAME = 'BranchTimeZone';

function detectTimeZone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

function supportedTimeZones(detected: string): string[] {
    const supported = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf;
    const zones = supported ? supported('timeZone') : [];

    return detected && !zones.includes(detected) ? [detected, ...zones] : zones;
}

/** What a save should do with the zone: nothing, or persist this value. */
export interface BranchTimeZoneSaveIntent {
    value: string;
    changed: boolean;
}

/**
 * The branch time zone state a branch editor needs: zones to offer, the zone to propose, and whether the
 * store has one at all. The value itself lives in the tenant and branch settings store, not on the branch.
 */
export class BranchTimeZoneSelection {
    readonly detected = detectTimeZone();

    readonly zones = supportedTimeZones(this.detected);

    private persisted: string | null = null;

    /**
     * Records the zone resolved for the branch and returns the zone the form should show. A store with no
     * zone of its own shows the browser's, which is a proposal the user has to confirm before it is saved.
     */
    load(settings: Setting[] | null | undefined): string {
        const resolved = settings?.find((setting) => setting.key === BRANCH_TIME_ZONE_SETTING_NAME)?.value;

        this.persisted = resolved ? resolved : null;

        return this.persisted ?? this.detected;
    }

    /**
     * Whether the store has no zone, and so a value is about to be set for the first time.
     */
    isUnset(): boolean {
        return this.persisted === null;
    }

    /**
     * Settles the zone a save should persist. An unset store prompts, so a detected value never reaches a
     * customer-facing booking time unconfirmed; a store that already has one saves the edited value
     * outright. Returns null when the user abandons the prompt, which abandons the save with it.
     */
    async resolveForSave(
        dialogService: DialogService,
        timeZoneId: string | null | undefined,
    ): Promise<BranchTimeZoneSaveIntent | null> {
        const selected = timeZoneId ?? this.detected;

        if (!this.isUnset()) {
            return { value: selected, changed: selected !== this.persisted };
        }

        const confirmed = await firstValueFrom(
            BranchTimeZoneModalComponent.open(dialogService, this.zones, selected, this.persisted),
        );

        return confirmed ? { value: confirmed, changed: true } : null;
    }

    /**
     * Records a persisted value so a second save in the same session does not prompt again.
     */
    markSaved(timeZoneId: string): void {
        this.persisted = timeZoneId;
    }
}
